import type { IMessage as IMessageChannelMessage, IMessageChannel } from '@curbnturf/entities';
import { HelperFunctions } from '@curbnturf/entities';
import type { IMessage, IThread, MessageAuthor } from '@cnt-workspace/models';

/**
 * Maps the real, relational `IMessageChannel` (numeric ids, `from`/`to` per
 * message, no explicit guest/host role field) onto cnt-landing's flat
 * `IThread` view model, so every existing consumer (inbox, notifications,
 * navbar/bottom-nav unread badge) keeps working unchanged against
 * `MessageService.threads$` regardless of the real API's shape.
 *
 * Known lossy/approximate mappings (the real model is coarser or
 * differently-shaped than the mock view model it replaces):
 *  - No explicit guest/host role exists server-side. `channel.user` is
 *    always the channel-initiating guest; the host is `channel.listing.user`
 *    (listing-only channel) or `channel.reservation.site.listing.user`
 *    (reservation-linked channel). If neither relation came back populated,
 *    we fall back to "not the guest ⇒ host" — safe because the server only
 *    ever returns channels the caller actually participates in.
 *  - `lastReadAt` has no real analog — the real model only tracks a
 *    per-message `read: boolean`, no read-*instant*. We backfill each
 *    participant's `lastReadAt` entry from the (epoch-seconds) `timestamp`
 *    of the newest message addressed to them that's marked read. This is an
 *    approximation (send time standing in for read time), omitted entirely
 *    when nothing addressed to that participant has been read yet.
 *  - No `'system'` message author exists in real data (the mock's
 *    booking-status system bubbles have no server-side equivalent — booking
 *    status stays visible via the inbox's timeline strip instead).
 */

const HOST_FALLBACK_LABEL = 'Host';
const GUEST_FALLBACK_LABEL = 'Guest';

function userFullName(user: { firstName?: string; lastName?: string } | undefined, fallback: string): string {
  const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  return name || fallback;
}

function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .map(p => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function hostUser(channel: IMessageChannel): { id?: number; email?: string; firstName?: string; lastName?: string } | undefined {
  return channel.listing?.user ?? channel.reservation?.site?.listing?.user;
}

function toIsoFromEpochSeconds(ts: number | undefined): string {
  if (!ts) return new Date().toISOString();
  return new Date(ts * 1000).toISOString();
}

function mapMessages(channel: IMessageChannel, guestId: number | undefined, threadId: string): IMessage[] {
  const messages = channel.messages ?? [];
  return messages
    .slice()
    .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0))
    .map((m: IMessageChannelMessage): IMessage => {
      const author: MessageAuthor = guestId != null && m.fromId === guestId ? 'guest' : 'host';
      return {
        id: String(m.id ?? ''),
        threadId,
        author,
        authorName: author === 'guest'
          ? userFullName(channel.user, GUEST_FALLBACK_LABEL)
          : userFullName(hostUser(channel), HOST_FALLBACK_LABEL),
        body: m.body,
        createdAt: toIsoFromEpochSeconds(m.timestamp),
      };
    });
}

/** Backfill an approximate lastReadAt map from per-message `read` flags —
 *  see header doc-comment for why this is a stand-in, not a literal field. */
function buildLastReadAt(
  channel: IMessageChannel,
  guestEmail: string,
  hostEmail: string,
  guestId: number | undefined,
  hostId: number | undefined,
): Record<string, string> {
  const lastReadAt: Record<string, string> = {};
  const messages = channel.messages ?? [];
  for (const m of messages) {
    if (!m.read) continue;
    const recipient = m.toId === guestId ? guestEmail : m.toId === hostId ? hostEmail : null;
    if (!recipient) continue;
    const iso = toIsoFromEpochSeconds(m.timestamp);
    if (!lastReadAt[recipient] || lastReadAt[recipient] < iso) lastReadAt[recipient] = iso;
  }
  return lastReadAt;
}

export function channelToThread(channel: IMessageChannel): IThread {
  const id = String(channel.id ?? '');
  const guestId = channel.userId ?? channel.user?.id;
  const host = hostUser(channel);
  const hostId = host?.id;
  const guestName = userFullName(channel.user, GUEST_FALLBACK_LABEL);
  const hostName = userFullName(host, HOST_FALLBACK_LABEL);
  const guestEmail = channel.user?.email ?? '';
  const hostEmail = host?.email ?? '';
  const messages = mapMessages(channel, guestId, id);
  const listing = channel.listing ?? channel.reservation?.site?.listing;
  const lastMessage = messages[messages.length - 1];

  return {
    id,
    bookingId: channel.reservationId ? `res-${channel.reservationId}` : undefined,
    listingId: listing?.id ?? 0,
    listingTitle: listing?.name || 'Listing',
    listingPhoto: listing?.photos?.[0] ? HelperFunctions.getPhotoUrlFullSize(listing.photos[0]) : '',
    guestEmail,
    guestName,
    guestInitials: initialsFromName(guestName) || 'G',
    hostEmail,
    hostName,
    hostInitials: initialsFromName(hostName) || 'H',
    messages,
    createdAt: toIsoFromEpochSeconds(channel.timestamp),
    updatedAt: lastMessage?.createdAt ?? toIsoFromEpochSeconds(channel.timestamp),
    lastReadAt: buildLastReadAt(channel, guestEmail, hostEmail, guestId, hostId),
    guestUserId: guestId,
    hostUserId: hostId,
  };
}

export function buildCreatePayloadForExistingChannel(channelId: number, body: string): IMessageChannelMessage {
  return { body, channel: { id: channelId } };
}

export function buildCreatePayloadForNewChannel(listingId: number, guestUserId: number, body: string): IMessageChannelMessage {
  return {
    body,
    channel: {
      listing: { id: listingId },
      user: { id: guestUserId },
    },
  };
}
